import { useState, useEffect, useCallback } from "react";
import { usePageSettings } from "../contexts/PageSettingsContext";

const useRedirect = () => {
	const [redirecting, setRedirecting] = useState(false);
	const [redirectSuccessful, setRedirectSuccessful] = useState(false);
	const { currency } = usePageSettings();

	const endPointUrl = process.env.GATSBY_CHECKOUT_ENDPOINT + "currency=" + currency;

	const redirect = useCallback(
		async (serviceType, iataCode, selectedPlan, webHostingPlanId) => {
			setRedirecting(true);
			try {
				let requestBody;
				switch (serviceType) {
					case "bgp":
						requestBody = { bgps: [iataCode] };
						break;
					case "ultraproxy":
						requestBody = { ultraproxies: [iataCode] };
						break;
					case "storage":
						requestBody = {
							resources: [
								{
									location_code: iataCode,
									service: serviceType,
									id_nas: selectedPlan.nas.id,
								},
							],
						};
						break;
					case "iptransit":
						requestBody = {
							resources: [
								{
									location_code: iataCode,
									service: serviceType,
									id_bandwidth: selectedPlan.bandwidth.id,
								},
							],
						};
						break;
					case "colocation":
						requestBody = {
							resources: [
								{
									location_code: iataCode,
									service: serviceType,
									id_rack: selectedPlan.rack.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_energy: selectedPlan.energy.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_bandwidth: selectedPlan.bandwidth.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_prefix: selectedPlan.prefix.id,
								},
							],
						};
						break;
					case "vps":
						requestBody = {
							resources: [
								{
									location_code: iataCode,
									service: serviceType,
									id_cpu: selectedPlan.cpu.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_ram: selectedPlan.ram.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_storage: selectedPlan.storage.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_bandwidth: selectedPlan.bandwidth.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_transfer: selectedPlan.transfer.id,
								},
							],
						};
						break;
					case "baremetal":
						requestBody = {
							resources: [
								{
									location_code: iataCode,
									service: serviceType,
									id_cpu: selectedPlan.cpu.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_ram: selectedPlan.ram.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_storage: selectedPlan.storage.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_bandwidth: selectedPlan.bandwidth.id,
								},
								{
									location_code: iataCode,
									service: serviceType,
									id_transfer: selectedPlan.transfer.id,
								},
							],
						};
						break;
					case "webhosting":
						requestBody = {
							plans: [webHostingPlanId],
						};
						break;
					default:
						// console.log("No redirect case for ", serviceType);
						setRedirecting(false);
						return false; // Exit the function for unknown service types
				}

				const response = await fetch(endPointUrl, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(requestBody),
				});
				const responseText = await response.text();

				if (response.ok) {
					// Use responseText to determine the redirect URL
					window.open(responseText, "_blank");
					setRedirectSuccessful(true);
					return true;
				} else {
					console.error("useRedirect API request failed:", response.statusText);
					return false;
					// Catch errors that might occur during the fetch operation,
					// such as network issues or invalid responses from the server.
				}
			} catch (error) {
				console.error("Error during useRedirect API request:", error);
				// The purpose of catching errors at this level is to ensure that the redirecting state is always set to false,
				//even if an error occurs. This prevents the application from being stuck in a loading state.
				setRedirecting(false);
				return false;
			}
		},
		[currency, endPointUrl]
	);

	useEffect(() => {
		if (redirectSuccessful) {
			setRedirecting(false);
		}
	}, [redirectSuccessful]);

	return { redirecting, redirect, redirectSuccessful };
};

export default useRedirect;
